import React, {useEffect} from 'react';
import styled from 'styled-components';
import {Helmet} from 'react-helmet';
import {useOutlet} from 'reconnect.js';
import 'antd/dist/antd.less';
import './Layout.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import AdminLayout from '../AdminLayout';
import ProfileLayout from '../ProfileLayout';
import CheckoutLayout from '../CheckoutLayout';
import MobileCheckoutLayout from '../MobileCheckoutLayout';
import SiteNavBar from '../SiteNavBar';
import SiteFooter from '../SiteFooter';
import {Font} from '@react-pdf/renderer';
import MessengerCustomerChat from 'react-messenger-customer-chat';
import Config from '../../../data.json';
import * as User from 'rev.sdk.js/Actions/User';
import * as AppActions from '../../AppActions';

function getBackgroundColor(pathname = '') {
  if (
    ['products', 'articles', 'product'].find((p) => pathname.indexOf(p) !== -1)
  ) {
    return '#F9F8F7';
  }

  return 'white';
}

Font.register({
  family: 'SourceHanSansCN',
  src: 'https://static.revtel-api.com/common/SourceHanSansCN-Regular.ttf',
});

function Layout({children, location, seo = {}}) {
  const [dimension] = useOutlet('dimension');
  const [initialized, setInitialized] = React.useState(false);

  React.useEffect(() => {
    const isAdmin = location.pathname.indexOf('admin') !== -1;

    const initialize = async (isAdmin) => {
      try {
        await User.autoLogin({admin: isAdmin});
      } catch (ex) {
        console.log('autoLogin ex', ex);
        await User.logout();
      }

      setInitialized(true);
    };

    if (!initialized) {
      initialize(isAdmin);
    }
  }, [initialized, location.pathname]);

  React.useLayoutEffect(() => {
    const gaScript = document.createElement('script');
    gaScript.async = true;
    gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${Config.gaId}`;
    document.head.appendChild(gaScript);

    window.dataLayer = window.dataLayer || [];
    window.gtag = function () {
      window.dataLayer.push(arguments);
    };
    window.gtag('js', new Date());
    window.gtag('config', Config.gaId, {debug_mode: true});
  }, []);

  if (location.pathname.indexOf('admin') > -1) {
    return <AdminLayout location={location}>{children}</AdminLayout>;
  } else if (location.pathname.indexOf('profile') > -1) {
    return (
      <Wrapper rwd={dimension.rwd}>
        <SiteNavBar location={location} />
        <ProfileLayout style={{flex: 1}} location={location}>
          {children}
        </ProfileLayout>
        <FBMessenger location={location} {...Config} />
        <SiteFooter />
      </Wrapper>
    );
  } else if (location.pathname.indexOf('/mobile/checkout') > -1) {
    return (
      <Wrapper rwd={dimension.rwd}>
        <MobileCheckoutLayout style={{flex: 1}} location={location}>
          {children}
        </MobileCheckoutLayout>
      </Wrapper>
    );
  } else if (location.pathname.indexOf('checkout') > -1) {
    return (
      <Wrapper rwd={dimension.rwd}>
        <SiteNavBar location={location} />
        <CheckoutLayout style={{flex: 1}} location={location}>
          {children}
        </CheckoutLayout>
        <SiteFooter />
      </Wrapper>
    );
  }

  let hasSiteNavBar = true;
  let hasSiteFooter = true;
  if (location.pathname.indexOf('/promo') === 0) {
    hasSiteNavBar = false;
    hasSiteFooter = false;
  }

  return (
    <>
      <Helmet>
        <title>{seo.title || '拼吧 PINBAR'}</title>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0"
        />
        <meta
          name="facebook-domain-verification"
          content="xohn16vad49r2wfskfpuz8fljjlwah"
        />

        <meta
          name="google-site-verification"
          content="U9X6U8yCEEBQPhz1McNwiQo2bhBjP3_gdAG9rwgUi-w"
        />

        {seo.keywords && <meta name="keywords" content={seo.keywords} />}
        {seo.title && <meta property="og:title" content={seo.title} />}
        {seo.description && (
          <meta name="description" content={seo.description} />
        )}
        {seo.image && <meta name="image" content={seo.image} />}
        {seo.description && (
          <meta property="og:description" content={seo.description} />
        )}
        {seo.image && <meta property="og:image" content={seo.image} />}
      </Helmet>
      <Wrapper rwd={dimension.rwd}>
        {hasSiteNavBar && <SiteNavBar bgColor="white" location={location} />}
        <div
          style={{
            flex: 1,
            backgroundColor: getBackgroundColor(location.pathname),
          }}>
          {children}
        </div>
        <FBMessenger location={location} {...Config} />
        {hasSiteFooter && <SiteFooter />}
      </Wrapper>
    </>
  );
}

const FBMessenger = ({
  location,
  facebookMessagerPageId,
  facebookMessagerAppId,
}) => {
  if (
    typeof window != undefined &&
    location &&
    facebookMessagerPageId &&
    facebookMessagerAppId
  ) {
    return (
      <MessengerCustomerChat
        pageId={facebookMessagerPageId}
        appId={facebookMessagerAppId}
        htmlRef={location.pathname}
        language="zh_TW"
      />
    );
  }

  return <div />;
};

const Wrapper = styled.div`
  --contentMaxWidth: 1200px;
  --contentMinHeight: 600px;
  --topNavBarHeight: 64px;
  --basePadding: 15px 20px;
  --primaryColor: #aace2d;
  --sectionPadding: 50px 100px;
  --sectionMobilePadding: 20px;

  min-height: 100vh;
  display: flex;
  flex-direction: column;

  .title {
    font-size: ${(props) => (props.rwd === 'desktop' ? 22 : 15)}px;
    color: var(--primaryColor);
    font-size: 22px;
    letter-spacing: 2px;
    font-weight: bold;
  }

  .row {
    display: flex;
    flex-direction: row;
  }

  .subtitle {
    color: var(--primaryColor);
    font-size: ${(props) => (props.rwd === 'desktop' ? 13 : 10)}px;
    letter-spacing: 2px;
    font-weight: bold;
  }

  .section {
    padding: ${(props) =>
      props.rwd === 'desktop'
        ? 'var(--sectionPadding)'
        : 'var(--sectionMobilePadding)'};
  }

  .carousel-wrapper {
    display: flex;
    flex-direction: ${(props) => (props.rwd === 'desktop' ? 'row' : 'column')};
    flex-wrap: wrap;
    justify-content: space-between;
  }

  .content {
    font-size: ${(props) => (props.rwd === 'desktop' ? 14 : 10)}px;
    letter-spacing: 2px;
    line-height: 22px;
    white-space: break-spaces;
    color: #000;
  }

  .centered {
    margin: 0 auto;
    text-align: center;
  }
`;

export default Layout;
